import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { getGuide } from '@/constants/corey';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem('diy_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (e) {
      console.error('Auth check failed:', e);
      localStorage.removeItem('diy_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('diy_token', res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const res = await api.post('/auth/signup', { email, password, name });
    localStorage.setItem('diy_token', res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('diy_token');
    setUser(null);
  }, []);

  const setAdminSession = useCallback((token, adminUser) => {
    localStorage.setItem('diy_token', token);
    setUser(adminUser);
  }, []);

  // Called by ResetPasswordPage after successful reset — stores fresh token + user.
  const finishReset = useCallback(({ token, user: freshUser }) => {
    if (token) localStorage.setItem('diy_token', token);
    if (freshUser) setUser(freshUser);
  }, []);

  const setGuide = useCallback(async (guideId) => {
    const res = await api.patch('/auth/me', { guide: guideId });
    setUser(res.data);
  }, []);

  const setPersonality = useCallback(async (personalityId) => {
    const res = await api.patch('/auth/me', { personality: personalityId });
    setUser(res.data);
  }, []);

  const setAdvanced = useCallback(async (isAdvanced) => {
    const res = await api.patch('/auth/me', { is_advanced: !!isAdvanced });
    setUser(res.data);
  }, []);

  const setMode = useCallback(async (mode) => {
    const res = await api.patch('/auth/me', { mode });
    setUser(res.data);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, setAdminSession, finishReset, setGuide, setPersonality, setAdvanced, setMode }),
    [user, loading, login, signup, logout, setAdminSession, finishReset, setGuide, setPersonality, setAdvanced, setMode]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Returns the current guide record for the logged-in user, or default (Corey).
export function useGuide() {
  const { user } = useContext(AuthContext) || {};
  return getGuide(user?.guide);
}
